import axios from 'axios';

// Toujours utiliser des URLs relatives pour que le proxy nginx/Vite gère le routage
// VITE_API_URL ne doit pas être /api sinon on obtiendrait /api/api/...
const API_URL = '';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Session : cookie HttpOnly posé par le backend (jamais lisible en JavaScript), envoyé
// automatiquement par le navigateur (même origine, withCredentials).

export interface IntegrationSettings {
  jiraUrl: string;
  jiraEmail: string;
  jiraApiTokenSet: boolean;
  jiraApiTokenFromDatabase: boolean;
  mondayApiKeySet: boolean;
  mondayApiKeyFromDatabase: boolean;
  dashboardBoardIds: number[];
  qaBoardIds: number[];
  boardsConfigured: boolean;
}

export interface IntegrationSettingsInput {
  jiraUrl: string;
  jiraEmail: string;
  jiraApiToken: string;
  mondayApiKey: string;
  dashboardBoardIds: number[];
  qaBoardIds: number[];
}

export interface VisiblePages {
  dashboard: boolean;
  users: boolean;
  support: boolean;
  epics: boolean;
  marketing: boolean;
  produit: boolean;
  pointHebdo: boolean;
  gestionUtilisateurs: boolean;
  performance: boolean;
  performanceDashboard: boolean;
  /** Page « Coûts horaires » ; donne aussi l'affichage des coûts dans le Suivi épic. */
  couts: boolean;
  /** Page « Suivi WIP » : WIP hebdomadaire et seuils par équipe. */
  wip: boolean;
}

/** Filtres par défaut Roadmap Adoria 2026 (préférences utilisateur) */
export type RoadmapAdoriaQuarterFilter = 'all' | 'Q1' | 'Q2' | 'Q3' | 'Q4';

export interface RoadmapAdoria2026DefaultFilters {
  trimestre: RoadmapAdoriaQuarterFilter;
  statut: string[];
  team: string[];
}

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  provider: 'local' | 'microsoft';
  role?: 'super_admin' | string | null;
  roleName?: string;
  visiblePages?: VisiblePages;
  teamId?: string | null;
  leadTeamIds?: string[];
  performanceGlobalAccess?: boolean;
  canManageTeamAssignment?: boolean;
}

export interface RoleDto {
  id: string;
  name: string;
  pageVisibilities: VisiblePages;
}

export interface UserWithRoleDto {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  provider: string;
  isActive: boolean;
  role: 'super_admin' | string | null;
  roleId: string | null;
  roleName: string;
}

/** Activity log entry */
export interface UserActivityLogEntry {
  id: string;
  type: 'login' | 'page_view' | 'error_500' | 'password_reset_request' | 'password_reset_complete';
  timestamp: string;
  meta?: { page?: string; durationMs?: number; path?: string; count?: number; emailSent?: boolean };
}

export interface AuthResponse {
  success: boolean;
  user?: User;
  /** True when the account was just created (first Microsoft login) */
  firstLogin?: boolean;
  error?: string;
  errors?: string[];
}

export interface MicrosoftConfig {
  enabled: boolean;
  clientId: string;
  tenantId: string;
  redirectUri: string;
}

export interface PasswordValidation {
  isValid: boolean;
  errors: string[];
  strength: 'weak' | 'medium' | 'strong' | 'very-strong';
  score: number;
}

export const authApi = {
  /**
   * Login with email and password
   */
  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/api/auth/login', {
        email,
        password
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      if (err.response?.data?.error) {
        return { success: false, error: err.response.data.error };
      }
      return { success: false, error: 'Erreur de connexion au serveur' };
    }
  },

  /**
   * Validate password strength (server-side)
   */
  async validatePassword(password: string): Promise<PasswordValidation | null> {
    try {
      const response = await api.post<{ success: boolean; validation: PasswordValidation }>(
        '/api/auth/validate-password',
        { password }
      );
      return response.data.validation;
    } catch (error) {
      console.error('Password validation error:', error);
      return null;
    }
  },

  /**
   * Get Microsoft SSO configuration
   */
  async getMicrosoftConfig(): Promise<MicrosoftConfig> {
    try {
      const response = await api.get<{ success: boolean } & MicrosoftConfig>(
        '/api/auth/microsoft/config'
      );
      return response.data;
    } catch (error) {
      return { enabled: false, clientId: '', tenantId: '', redirectUri: '' };
    }
  },

  /**
   * Handle Microsoft SSO callback
   */
  async microsoftCallback(idToken: string, nonce: string): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/api/auth/microsoft/callback', {
        idToken,
        nonce
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      if (err.response?.data?.error) {
        return { success: false, error: err.response.data.error };
      }
      return { success: false, error: 'Erreur de connexion Microsoft' };
    }
  },

  /**
   * Déconnexion : le backend efface le cookie de session HttpOnly.
   */
  async logout(): Promise<void> {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // non bloquant : l'état local est réinitialisé de toute façon
    }
  },

  /**
   * Verify current token validity
   */
  async verifyToken(): Promise<boolean> {
    try {
      const response = await api.get<{ success: boolean; valid: boolean }>('/api/auth/verify');
      return response.data.valid;
    } catch (error) {
      return false;
    }
  },

  /**
   * Get current user info (with role and visiblePages)
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await api.get<{ success: boolean; user: User }>('/api/auth/me');
      return response.data.user;
    } catch (error) {
      return null;
    }
  },

  /**
   * List users and roles (super_admin only)
   */
  async getUsersAndRoles(): Promise<{ users: UserWithRoleDto[]; roles: RoleDto[] }> {
    const response = await api.get<{ success: boolean; users: UserWithRoleDto[]; roles: RoleDto[] }>('/api/auth/users');
    if (!response.data.success) throw new Error((response.data as { error?: string }).error);
    return { users: response.data.users, roles: response.data.roles };
  },

  /**
   * Crée un compte local dans l'organisation de l'admin et envoie une invitation (super_admin only).
   * Remplace l'inscription libre.
   */
  async inviteLocalUser(input: {
    email: string;
    firstName?: string;
    lastName?: string;
    roleId?: string;
  }): Promise<{ success: boolean; userId?: string; emailSent?: boolean; error?: string }> {
    try {
      const response = await api.post<{ success: boolean; userId: string; emailSent: boolean }>('/api/auth/users', input);
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; errors?: string[] } } };
      const msg = err.response?.data?.error || err.response?.data?.errors?.join('. ');
      return { success: false, error: msg || 'Erreur de connexion au serveur' };
    }
  },

  /**
   * Export de mes données personnelles (RGPD, accès / portabilité).
   */
  async exportMyData(): Promise<Record<string, unknown>> {
    const response = await api.get<{ success: boolean; data: Record<string, unknown> }>('/api/auth/me/export');
    return response.data.data;
  },

  /**
   * Export des données d'un collaborateur (super_admin only, demande d'accès).
   */
  async exportUserData(userId: string): Promise<Record<string, unknown>> {
    const response = await api.get<{ success: boolean; data: Record<string, unknown> }>(`/api/auth/users/${userId}/export`);
    return response.data.data;
  },

  /**
   * Effacement d'un compte : anonymisation irréversible (super_admin only). `confirmEmail` doit
   * être l'email du compte (garde-fou).
   */
  async deleteUser(userId: string, confirmEmail: string): Promise<void> {
    await api.delete(`/api/auth/users/${userId}`, { data: { confirmEmail } });
  },

  /**
   * Active / désactive un compte (super_admin only). La désactivation révoque ses sessions.
   */
  async setUserActive(userId: string, isActive: boolean): Promise<boolean> {
    const response = await api.patch<{ success: boolean; isActive: boolean }>(`/api/auth/users/${userId}/status`, { isActive });
    if (!response.data.success) throw new Error((response.data as { error?: string }).error);
    return response.data.isActive;
  },

  /**
   * Update user role (super_admin only)
   */
  async updateUserRole(userId: string, role: 'super_admin' | null, roleId: string | null): Promise<User> {
    const body: { role?: 'super_admin'; roleId?: string | null } = {};
    if (role === 'super_admin') body.role = 'super_admin';
    else body.roleId = roleId;
    const response = await api.patch<{ success: boolean; user: User }>(`/api/auth/users/${userId}`, body);
    if (!response.data.success) throw new Error((response.data as { error?: string }).error);
    return response.data.user;
  },

  /**
   * List roles (super_admin only)
   */
  async getRoles(): Promise<RoleDto[]> {
    const response = await api.get<{ success: boolean; roles: RoleDto[] }>('/api/auth/roles');
    if (!response.data.success) throw new Error((response.data as { error?: string }).error);
    return response.data.roles;
  },

  /**
   * Create role (super_admin only)
   */
  async createRole(name: string, pageVisibilities: VisiblePages): Promise<RoleDto> {
    const response = await api.post<{ success: boolean; role: RoleDto }>('/api/auth/roles', { name, pageVisibilities });
    if (!response.data.success) throw new Error((response.data as { error?: string }).error);
    return response.data.role;
  },

  /**
   * Update role (super_admin only)
   */
  async getIntegrationSettings(): Promise<IntegrationSettings> {
    const response = await api.get<{ success: boolean; settings: IntegrationSettings }>('/api/auth/integration-settings');
    if (!response.data.success) throw new Error((response.data as { error?: string }).error);
    return response.data.settings;
  },

  async saveIntegrationSettings(input: IntegrationSettingsInput): Promise<IntegrationSettings> {
    const response = await api.put<{ success: boolean; settings: IntegrationSettings }>(
      '/api/auth/integration-settings',
      input
    );
    if (!response.data.success) throw new Error((response.data as { error?: string }).error);
    return response.data.settings;
  },

  async updateRole(roleId: string, data: { name?: string; pageVisibilities?: VisiblePages }): Promise<RoleDto> {
    const response = await api.patch<{ success: boolean; role: RoleDto }>(`/api/auth/roles/${roleId}`, data);
    if (!response.data.success) throw new Error((response.data as { error?: string }).error);
    return response.data.role;
  },

  /**
   * Record a page view for the current user (authenticated). Deduplicated per page per minute.
   */
  async recordPageView(page: string): Promise<void> {
    try {
      await api.post<{ success: boolean }>('/api/auth/me/page-view', { page });
    } catch {
      // Non-blocking: do not break navigation if tracking fails
    }
  },

  /**
   * Filtres par défaut Roadmap Adoria 2026 (trimestre + statut + team) pour l'utilisateur connecté.
   */
  async getRoadmapAdoria2026DefaultFilters(): Promise<RoadmapAdoria2026DefaultFilters> {
    const response = await api.get<{ success: boolean; filters: RoadmapAdoria2026DefaultFilters }>(
      '/api/auth/me/preferences/roadmap-adoria-2026-filters'
    );
    if (!response.data.success) {
      throw new Error((response.data as { error?: string }).error || 'Impossible de charger les filtres');
    }
    return response.data.filters;
  },

  /**
   * Enregistre les filtres par défaut Roadmap Adoria 2026 pour l'utilisateur connecté.
   */
  async saveRoadmapAdoria2026DefaultFilters(
    filters: RoadmapAdoria2026DefaultFilters
  ): Promise<RoadmapAdoria2026DefaultFilters> {
    const response = await api.put<{ success: boolean; filters: RoadmapAdoria2026DefaultFilters }>(
      '/api/auth/me/preferences/roadmap-adoria-2026-filters',
      filters
    );
    if (!response.data.success) {
      throw new Error((response.data as { error?: string }).error || 'Impossible d\'enregistrer les filtres');
    }
    return response.data.filters;
  },

  /**
   * Get activity logs for a user (super_admin only). Includes login timestamps; later page_view and error_500.
   */
  async getUserLogs(userId: string, limit = 100): Promise<UserActivityLogEntry[]> {
    const response = await api.get<{ success: boolean; logs: UserActivityLogEntry[] }>(
      `/api/auth/users/${userId}/logs`,
      { params: { limit } }
    );
    if (!response.data.success) throw new Error((response.data as { error?: string }).error);
    return response.data.logs;
  },

  /**
   * Get page view stats for a user (super_admin only): daily counts, totals, percentages.
   */
  async getUserPageStats(
    userId: string,
    days = 30
  ): Promise<{ pages: Record<string, number>; total: number; percentages: Record<string, number>; daily: Array<{ date: string; pages: Record<string, number>; total: number }> }> {
    const response = await api.get<{
      success: boolean;
      pages: Record<string, number>;
      total: number;
      percentages: Record<string, number>;
      daily: Array<{ date: string; pages: Record<string, number>; total: number }>;
    }>(`/api/auth/users/${userId}/page-stats`, { params: { days } });
    if (!response.data.success) throw new Error((response.data as { error?: string }).error);
    return {
      pages: response.data.pages,
      total: response.data.total,
      percentages: response.data.percentages,
      daily: response.data.daily ?? []
    };
  },

  /**
   * Demande un email de réinitialisation de mot de passe.
   * Répond toujours avec succès (anti-énumération d'email).
   */
  async forgotPassword(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      await api.post('/api/auth/forgot-password', { email });
      return { success: true };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      return {
        success: false,
        error: err.response?.data?.error || 'Erreur lors de la demande de réinitialisation'
      };
    }
  },

  /**
   * Réinitialise le mot de passe avec un token valide.
   */
  async resetPassword(token: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
      await api.post('/api/auth/reset-password', { token, password });
      return { success: true };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      return {
        success: false,
        error: err.response?.data?.error || 'Erreur lors de la réinitialisation du mot de passe'
      };
    }
  }
};

